import { RECORD_PLAINTEXT_KEY } from '@/configs/config';
import { VITE_ALEO_TOKEN_REGISTRY, VITE_NETWORK_TYPE } from '@/configs/env';
import { getTokenIdfromTokenDetails, tNetworkTypes, TOKEN_DETAILS } from '@/configs/token';
import { useAleoWallet } from '@/hooks/useAleoWallet';
import { useProgram } from '@/hooks/useProgram';
import { js2leo, leo2js } from '@/lib/aleo';
import { parseAleoObject, TokenRecordPlainText } from '@/utils/aleoRecordParser';
import { useQuery } from '@tanstack/react-query';
import { ValueOf } from 'type-fest';

export type WalletTokenMap = Record<string, { tokens: any[]; metadata: ValueOf<typeof TOKEN_DETAILS> }>;

const getEmptyTokenRecordsMap = (): WalletTokenMap =>
  Object.values(TOKEN_DETAILS).reduce(
    (acc, token) => ({ ...acc, [token.tokenId[VITE_NETWORK_TYPE]]: { tokens: [], metadata: token } }),
    {}
  );

export const walletTokensQueryKeys = {
  all: ['wallet-tokens'] as const,
  byWallet: (address: string, walletName: string) => [...walletTokensQueryKeys.all, address, walletName] as const,
};

export function useWalletTokensQuery() {
  const { address, wallet } = useAleoWallet();
  const { getProgramRecords } = useProgram();
  const walletName = wallet?.adapter?.name;
  const recordKey = walletName ? RECORD_PLAINTEXT_KEY[walletName as keyof typeof RECORD_PLAINTEXT_KEY] : undefined;

  return useQuery<WalletTokenMap>({
    queryKey: walletTokensQueryKeys.byWallet(address ?? 'disconnected', walletName ?? 'no-wallet'),
    enabled: Boolean(address && recordKey),
    queryFn: async () => {
      if (!address || !recordKey) return getEmptyTokenRecordsMap();

      const records = await getProgramRecords(VITE_ALEO_TOKEN_REGISTRY);
      const supportedTokenIds = getTokenIdfromTokenDetails(VITE_NETWORK_TYPE as tNetworkTypes);
      const tokenRecordsMap = getEmptyTokenRecordsMap();

      const filteredTokenRecords = records
        .filter((record) => {
          const parsedRecord = parseAleoObject((record as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
          return Boolean(parsedRecord.token_id);
        })
        .filter((record) => {
          const parsedRecord = parseAleoObject((record as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
          const recordTokenId = leo2js.replaceValue(parsedRecord.token_id);
          return supportedTokenIds.includes(js2leo.field(BigInt(recordTokenId)));
        });

      filteredTokenRecords.forEach((record) => {
        const parsedRecord = parseAleoObject((record as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
        const tokenId = leo2js.replaceValue(parsedRecord.token_id);
        const recordTokenId = js2leo.field(BigInt(tokenId));
        if (parsedRecord.amount && tokenRecordsMap[recordTokenId]) {
          tokenRecordsMap[recordTokenId].tokens.push(record);
        }
      });

      return Object.fromEntries(
        Object.entries(tokenRecordsMap).map(([key, records]) => [
          key,
          {
            ...records,
            tokens: records.tokens
              .sort((a, b) => {
                const parsedA = parseAleoObject((a as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
                const parsedB = parseAleoObject((b as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
                const initial = leo2js.u128(parsedA.amount);
                const latter = leo2js.u128(parsedB.amount);
                return latter < initial ? -1 : latter > initial ? 1 : 0;
              })
              .map((record) => {
                const parsedRecord = parseAleoObject((record as unknown as Record<string, string>)[recordKey!]) as TokenRecordPlainText;
                return {
                  ...record,
                  data: {
                    owner: leo2js.address(parsedRecord.owner),
                    token_id: leo2js.replaceValue(parsedRecord.token_id),
                    amount: leo2js.u128(parsedRecord.amount),
                    external_authorization_required: leo2js.boolean(parsedRecord.external_authorization_required),
                    authorized_until: leo2js.u128(parsedRecord.authorized_until),
                  },
                };
              }),
          },
        ])
      );
    },
  });
}
